// bluaIQ — Project Location Scope resolver
//
// Single resolver that future code/jurisdiction features call to
// figure out where a project is and what jurisdiction-scoped data
// (pricing pack, code pack, inspection sequence, required-uploads
// pack, lifecycle pack) applies.
//
// Today only state + city + zip are stored on Project; county and
// neighborhood/overlay slots are present in the returned shape but
// will remain `null` until the schema and project-creation form
// gain those fields. The resolver is forward-compatible.
//
// SAFETY / GENERALISATION:
//   * No DFW/Texas hardcoding in PRODUCTION LOGIC. Texas/DFW only
//     appears here as the **scoped allow-list** that decides whether
//     the existing TX-DFW seed pack may be applied. Outside that
//     allow-list the resolver returns no metro / no jurisdictionKey,
//     and downstream callers must surface "manual quote required" /
//     "fallback guidance — subject to AHJ and professional review"
//     instead of guessing.
//   * No "code compliant" / "permit guaranteed" claims. The resolver
//     never asserts compliance; it only carries `confidence` and
//     `fallbackUsed` so callers can phrase their output safely.
//   * The supported-DFW-city allow-list is intentionally narrow and
//     aligned with the seven cities for which jurisdiction packs
//     already exist (`src/takeoff-v2/jurisdictions/dfw.ts`). When
//     packs expand, this allow-list expands the same way — by
//     adding scoped, versioned, replaceable seed data, not by
//     renaming a global default.

export interface ProjectLocationInput {
  state?: string | null
  city?: string | null
  zipCode?: string | null
  address?: string | null
  county?: string | null            // reserved — schema does not store it yet
  neighborhood?: string | null      // reserved — schema does not store it yet
  jurisdictionKey?: string | null   // reserved — explicit AHJ override
}

export type LocationConfidence = 'explicit' | 'derived' | 'unknown'

export interface ProjectLocationScope {
  /** Two-letter state code, uppercased, or null when not provided. */
  state: string | null
  /** County name (lower-case). Reserved — null until the schema gains a county field. */
  county: string | null
  /** City name (lower-case, normalised). */
  city: string | null
  /** First 5 digits of the zip code, or null. */
  zip: string | null
  /** Neighborhood / subdivision / overlay (lower-case). Reserved. */
  neighborhood: string | null
  /** High-level metro grouping when one is recognised. Today only `'DFW'`. */
  metro: 'DFW' | null
  /** Stable jurisdiction key (e.g. `tx-dallas`) when a known city is matched. */
  jurisdictionKey: string | null
  /**
   * Confidence in the resolved scope:
   *   - `explicit`: state + city present and city is in a recognised pack.
   *   - `derived`:  state + city present but no recognised city pack
   *                 (e.g. Texas project outside the supported DFW set).
   *   - `unknown`:  not enough location data to resolve a jurisdiction.
   */
  confidence: LocationConfidence
  /**
   * True when the caller will have to fall back to a less-specific
   * pack (city → county → state → national/model-code → product/manufacturer)
   * or to manual / professional / AHJ review. Callers that surface
   * jurisdiction-aware output to a customer must mark output as
   * "fallback guidance — subject to AHJ and professional review"
   * whenever this flag is true.
   */
  fallbackUsed: boolean
}

/**
 * Supported DFW cities (lower-case, no whitespace). Aligned with
 * the existing `DFW_JURISDICTION_PACKS` keys in
 * `src/takeoff-v2/jurisdictions/dfw.ts`. Adding a city here without
 * also adding the corresponding jurisdiction pack would be a
 * regression — keep the two sets aligned.
 */
const SUPPORTED_DFW_CITY_KEYS: ReadonlySet<string> = new Set([
  'dallas',
  'frisco',
  'plano',
  'mckinney',
  'allen',
  'celina',
  'prosper',
])

/** Normalise a city string to the canonical lower-case key (no whitespace, no punctuation). */
function normalizeCityKey(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim().toLowerCase()
  if (!trimmed) return null
  // Strip whitespace and punctuation so "Mc Kinney" → "mckinney" and
  // "St. Paul" → "stpaul". Letters and digits only.
  return trimmed.replace(/[^a-z0-9]/g, '')
}

/** Normalise a state string to a two-letter upper-case code, or null. */
function normalizeStateCode(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim().toUpperCase()
  if (trimmed.length !== 2) return null
  return trimmed
}

/** Extract the first 5 digits of a zip code, or null. */
function normalizeZip5(raw: string | null | undefined): string | null {
  if (!raw) return null
  const digits = raw.replace(/\D/g, '')
  if (digits.length < 5) return null
  return digits.slice(0, 5)
}

/**
 * Resolve a normalised location scope from the project's location
 * fields. This is the single source of truth that future jurisdiction
 * data look-ups should consult; callers MUST NOT branch on raw
 * `state === 'TX'` etc. directly.
 *
 * The resolver is intentionally pure (no DB, no network) so it can
 * be called freely from request handlers, tests, and the future
 * jurisdiction-pack pipeline.
 */
export function resolveProjectLocationScope(
  input: ProjectLocationInput | null | undefined,
): ProjectLocationScope {
  const state = normalizeStateCode(input?.state)
  const cityKey = normalizeCityKey(input?.city)
  const zip = normalizeZip5(input?.zipCode)
  const county = input?.county ? input.county.trim().toLowerCase() : null
  const neighborhood = input?.neighborhood ? input.neighborhood.trim().toLowerCase() : null

  // ── unknown: no usable signal ──────────────────────────────────
  if (!state && !cityKey && !zip) {
    return {
      state: null,
      county,
      city: cityKey,
      zip,
      neighborhood,
      metro: null,
      jurisdictionKey: null,
      confidence: 'unknown',
      fallbackUsed: true,
    }
  }

  // ── explicit DFW match: state==='TX' + recognised city key ─────
  if (state === 'TX' && cityKey && SUPPORTED_DFW_CITY_KEYS.has(cityKey)) {
    return {
      state,
      county,
      city: cityKey,
      zip,
      neighborhood,
      metro: 'DFW',
      // Stable key shape: <state>-<city>. Future jurisdiction packs
      // can expose the same shape to keep look-ups consistent.
      jurisdictionKey: `tx-${cityKey}`,
      confidence: 'explicit',
      fallbackUsed: false,
    }
  }

  // ── derived: state+city present but no recognised city pack ────
  if (state && cityKey) {
    return {
      state,
      county,
      city: cityKey,
      zip,
      neighborhood,
      metro: null,
      jurisdictionKey: null,
      confidence: 'derived',
      fallbackUsed: true,
    }
  }

  // ── partial: state OR city OR zip but not enough for a match ───
  return {
    state,
    county,
    city: cityKey,
    zip,
    neighborhood,
    metro: null,
    jurisdictionKey: null,
    confidence: 'unknown',
    fallbackUsed: true,
  }
}
